import Link from 'next/link';
import React, { FormEvent, useState } from 'react';
import styles from './consultantContact.module.scss';
import LargeWidthContainer from '../Layout/Containers/LargeWidthContainer';
import { ContactFormData, ContactProps, WhoOptions } from './ContactModels';
import { MediaComponent } from '../util/MediaComponent/MediaComponent';

const ConsultantContactComponent = ({ extraStyles }: ContactProps) => {
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [message, setMessage] = useState('');
    const [firstNameInvalid, setFirstNameInvalid] = useState(false);
    const [emailInvalid, setEmailInvalid] = useState(false);

    const [messageSentSuccessfully, setMessageSentSuccessfully] = useState(false);
    const [messageSentError, setMessageSentError] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const sentContactEmail = (formData: ContactFormData) => {
        return fetch(`${process.env.NEXT_PUBLIC_API_URL}/send-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(formData),
        });
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        setFirstNameInvalid(firstName.length === 0);
        setEmailInvalid(email.length === 0);

        if (email.length > 0 && firstName.length > 0) {
            const formData: ContactFormData = {
                firstName,
                email,
                company,
                message,
                who: WhoOptions.ENERGY_OFFSHORE,
            };

            setSubmitting(true);
            setMessageSentSuccessfully(false);

            sentContactEmail(formData)
                .then((response) => {
                    setSubmitting(false);
                    if (response.status == 200) {
                        setMessageSentSuccessfully(true);
                        setMessageSentError(false);
                    } else {
                        setMessageSentSuccessfully(false);
                        setMessageSentError(true);
                    }
                })
                .catch((error) => {
                    setMessageSentSuccessfully(false);
                    setMessageSentError(true);
                    setSubmitting(false);
                });
        }
    };

    return (
        <section style={extraStyles}>
            <LargeWidthContainer extraClassNames={styles.contactContainer}>
                <div className={styles.contactBackground} />
                <div className={styles.doubleColumn}>
                    <div className={styles.child}>
                        <span className={styles.subIngress}>Let's talk</span>
                        <h1 className={styles.ingress}>
                            What would you like
                            <br /> to move forward?
                        </h1>
                        <p className={styles.description}>
                            Are you planning a system change, looking for senior expertise, or
                            exploring AI and automation? Tell Hilda a little about your situation,
                            and she will get in touch to discuss how Noria can help.
                        </p>
                        <p className={styles.subDescription}>
                            We start by understanding what you want to achieve, what is getting in
                            the way and which expertise you may need.
                        </p>
                        <div className={styles.person}>
                            <MediaComponent
                                extraClassNames={styles.avatar}
                                maxHeight={150}
                                maxWidth={300}
                                src="/images/hilda.png"
                                alt="Hilda Beate Lygre"
                            />
                            <div className={styles.who}>
                                <div className={styles.name}>Hilda Beate Lygre</div>
                                <div className={styles.role}>
                                    Your contact for energy and offshore
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.child}>
                        <form className={styles.form} onSubmit={onSubmit} name="contact" noValidate>
                            <div className={styles.row}>
                                <label className={styles.field}>
                                    <span className={styles.label}>Name*</span>
                                    <input
                                        className={`${styles.input} ${firstNameInvalid ? styles.invalid : ''}`}
                                        type="text"
                                        name="name"
                                        autoComplete="name"
                                        required
                                        value={firstName}
                                        onChange={(e) => {
                                            setFirstName(e.target.value);
                                            setFirstNameInvalid(false);
                                        }}
                                    />
                                    {firstNameInvalid && (
                                        <span className={styles.error}>Please enter your name</span>
                                    )}
                                </label>
                                <label className={styles.field}>
                                    <span className={styles.label}>Work email*</span>
                                    <input
                                        className={`${styles.input} ${emailInvalid ? styles.invalid : ''}`}
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailInvalid(false);
                                        }}
                                    />
                                    {emailInvalid && (
                                        <span className={styles.error}>
                                            Please enter your work email
                                        </span>
                                    )}
                                </label>
                            </div>
                            <label className={styles.field}>
                                <span className={styles.label}>Company</span>
                                <input
                                    className={styles.input}
                                    type="text"
                                    name="company"
                                    autoComplete="organization"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.label}>
                                    What would you like to discuss?
                                </span>
                                <span className={styles.optional}>(optional)</span>
                                <textarea
                                    className={styles.textarea}
                                    name="message"
                                    rows={4}
                                    aria-describedby="message-hint"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                />
                                <span id="message-hint" className={styles.hint}>
                                    For example, a system change, a need for senior expertise, or an
                                    AI and automation initiative.
                                </span>
                            </label>
                            <p className={styles.privacy}>
                                We use your details to respond to your enquiry. Read our{' '}
                                <Link
                                    href="/privacy"
                                    title="Go to the Privacy and Cookie policy page"
                                >
                                    privacy notice
                                </Link>
                                .
                            </p>
                            <div>
                                <button
                                    type="submit"
                                    className={`nv-buton nv-button-primary ${styles.submit}`}
                                    disabled={submitting}
                                >
                                    Send to Hilda ➔
                                </button>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontSize: '14px',
                                        marginTop: '10px',
                                    }}
                                >
                                    {messageSentError ? (
                                        <div style={{ color: 'var(--primary-red)' }}>
                                            We can't reach out to you. Please try again!
                                        </div>
                                    ) : null}

                                    {messageSentSuccessfully ? (
                                        <div style={{ color: 'var(--primary-green' }}>
                                            Thank you for your inquiry. We will get back to you as
                                            soon as possible.
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </LargeWidthContainer>
        </section>
    );
};
export default ConsultantContactComponent;
